import type { FC } from 'react';

import useGlobalPermissions from '@zen/Auth/useGlobalPermissions';
import useAccount from '@zen/utils/hooks/useAccount';

import { type Account, AccountStatus } from '../types';
import FinancialsSettingsDetails from './AccountFinancialDetails';
import ActivateAccount from './ActivateAccount';
import AnalyticsSettingsDetails from './AnalyticsSettingsDetails';
import DeactivateAccount from './DeactivateAccount';
import DemoAccountDetails from './DemoAccountDetails';
import InternalSupplyDetails from './InternalSupplyDetails';
import LegalDetails from './LegalDetails';
import MainDetails from './MainDetails';
import { OrganizationalRoleEnum } from './types';

interface Props {
  accountDetails: Account;
}
const GeneralAccountDetails: FC<Props> = ({ accountDetails }) => {
  const {
    businessUnits,
    demoMode,
    hasBusinessUnitsThatSupplyEachOther,
    id: accountId,
    legalInformation,
    organizationalRole,
    permissions,
    referencePrefix,
    status,
    tradingName
  } = accountDetails;

  const { check } = useGlobalPermissions();
  const {
    userProfile: { isAdmin }
  } = useAccount();

  const canActivateAccount: boolean = check('accounts.canActivate');

  const isAccountInactive: boolean = status === AccountStatus.INACTIVE;
  const hasBusinessUnits: boolean = businessUnits.length > 0;

  const canDeactivateAccount: boolean = !!permissions?.settings?.canDeactivate?.value;
  const canEditMainDetails: boolean = !!permissions?.settings?.canEditAccountDetails?.value;
  const canEditLegalDetails: boolean = !!permissions?.settings?.canEditLegalDetails?.value;
  const canToggleDemoModeAccount: boolean = !!permissions?.settings?.canEditDemoMode?.value;
  const canToggleDemoModeGlobal: boolean = check('accounts.canUpdateDemoMode');
  const canToggleIntraCompanyTrade: boolean = !!permissions?.settings?.canInviteConnection?.value;
  const canViewConnections: boolean = !!permissions?.settings?.canViewConnections;
  const isAnalyticsSettingOrganisationType: boolean =
    organizationalRole === OrganizationalRoleEnum.CONSIGNMENT_PARTY || organizationalRole === OrganizationalRoleEnum.ZENCARGO;
  const shouldRenderAnalyticsSection: boolean = isAdmin && isAnalyticsSettingOrganisationType;

  const displayToggleDemoMode = (canToggleDemoModeGlobal && isAccountInactive) || canToggleDemoModeAccount;
  const displayIntraCompanyTrade = hasBusinessUnits && canViewConnections;

  return (
    <div className="grid grid-cols-8 gap-8 xl:gap-6 xl:grid-cols-12">
      <MainDetails
        accountId={accountId}
        canEdit={canEditMainDetails}
        referencePrefix={referencePrefix}
        tradingName={tradingName}
      />
      <LegalDetails {...legalInformation} accountId={accountId} canEdit={canEditLegalDetails} />
      {shouldRenderAnalyticsSection && <AnalyticsSettingsDetails />}
      {canEditMainDetails && <FinancialsSettingsDetails accountId={accountId} />}
      {displayToggleDemoMode && <DemoAccountDetails accountId={accountId} canEdit={!isAccountInactive} demoMode={demoMode} />}
      {displayIntraCompanyTrade && (
        <InternalSupplyDetails
          accountId={accountId}
          canEdit={canToggleIntraCompanyTrade}
          hasBusinessUnitsThatSupplyEachOther={!!hasBusinessUnitsThatSupplyEachOther}
        />
      )}
      {canActivateAccount && isAccountInactive && <ActivateAccount accountId={accountId} />}
      {canDeactivateAccount && !isAccountInactive && <DeactivateAccount accountId={accountId} tradingName={tradingName} />}
    </div>
  );
};

export default GeneralAccountDetails;
